// PATH: src/components/chapters/ChapterDrawer.tsx
import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";

import type { Document } from "../../models/domain";
import type { DocumentObjective } from "../../api/objectives/objectives";
import { useDocumentsApi, type ChapterSourcePart } from "../../api/documents/documents";
import { EmptyState, StatusPill } from "../ui";
import { formatChapterPosition } from "../documents/documentChapter";
import { useChapterReview } from "../documents/useChapterReview";
import ChapterObjectivesTab from "../documents/ChapterObjectivesTab";
import ObjectiveKnowledgePanel from "../documents/ObjectiveKnowledgePanel";
import ChapterLessonsTab from "../documents/ChapterLessonsTab";
import ChapterInfoTab from "../documents/ChapterInfoTab";
import ChapterRelectureTab from "../documents/ChapterRelectureTab";
import { useAuth } from "../../context/AuthContext";
import { REVIEW_LABELS, REVIEW_TONES, asReviewStatus } from "./reviewStatus";
import type { CurriculumNames } from "./chapterFilters";

export type ChapterTab = "objectives" | "lessons" | "review" | "info";

interface Props {
  /** Chapitre affiché ; null = panneau fermé. */
  document: Document | null;
  /** Le document est connu par son id (lien partagé) mais pas encore chargé. */
  loading?: boolean;
  /** Lien vers un chapitre introuvable dans ce programme. */
  notFound?: boolean;
  tab: ChapterTab;
  onTabChange: (tab: ChapterTab) => void;
  names: CurriculumNames;
  onClose: () => void;
  onDownload: (doc: Document, part: ChapterSourcePart) => void;
  /** Le chapitre a changé (titre, état, identité) : la liste suit. */
  onChanged: (doc: Document) => void;
  /** Une extraction d'objectifs a abouti : la complétude est à relire. */
  onObjectivesExtracted?: () => void;
}

/**
 * Détail d'un chapitre : un seul panneau à droite, quatre onglets. La base de
 * faits d'un objectif s'ouvre DANS le panneau (vue de détail avec retour),
 * pas dans une fenêtre de plus.
 */
export const ChapterDrawer: React.FC<Props> = ({
  document,
  loading,
  notFound,
  tab,
  onTabChange,
  names,
  onClose,
  onDownload,
  onChanged,
  onObjectivesExtracted,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { getLatestDocumentVersion } = useDocumentsApi();
  // Relecteur : pas d'onglet Informations (fiche modifiable), le reste en lecture.
  const { canEdit } = useAuth();

  const docId = document?.id ?? null;
  const review = useChapterReview(docId);
  const [focused, setFocused] = React.useState<DocumentObjective | null>(null);
  // Onglet d'où la base de faits a été ouverte : « retour » y ramène.
  const [returnTab, setReturnTab] = React.useState<ChapterTab>("objectives");
  const [version, setVersion] = React.useState<number | null>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setFocused(null);
    setVersion(null);
    if (!docId) return;
    let cancelled = false;
    getLatestDocumentVersion(docId)
      .then(({ data }) => !cancelled && setVersion(data?.version ?? null))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  // Changer de vue remet le panneau en haut.
  React.useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [tab, focused]);

  const openObjective = (objective: DocumentObjective, from: ChapterTab = "objectives") => {
    setFocused(objective);
    setReturnTab(from);
    onTabChange("objectives");
  };

  /* Clic sur un objectif dans l'onglet Leçons : l'objet complet si la liste
     le connaît, sinon le minimum que la base de faits lit. */
  const openObjectiveById = (objectiveId: string, text: string) => {
    const found = review.objectives.find((o) => o.id === objectiveId);
    openObjective(
      found ??
        ({
          id: objectiveId,
          document_id: docId,
          doc_version_id: "",
          ordinal: 0,
          text,
        } as unknown as DocumentObjective),
      "lessons",
    );
  };

  // Leçons à reprendre (vides + faibles) : compte affiché sur l'onglet.
  const lessonIssues =
    review.lessonsState === "ok" && review.lessons ? review.lessons.summary.empty + review.lessons.summary.weak : 0;
  const lessonIssuesSevere = review.lessonsState === "ok" && (review.lessons?.summary.empty ?? 0) > 0;

  const reviewStatus = asReviewStatus(document?.review_status);

  const position = document ? formatChapterPosition(document) : "";
  const context = document
    ? [
        names.grade(document.grade_id),
        names.subject(document.subject_id),
        names.tracksOfGrade(document.grade_id).length > 0 || document.track_id ? names.track(document.track_id) : null,
        position || null,
      ].filter(Boolean)
    : [];

  const renderBody = () => {
    if (!document) return null;
    if (tab === "objectives") {
      return focused ? (
        <ObjectiveKnowledgePanel
          objective={focused}
          onBack={() => {
            setFocused(null);
            onTabChange(returnTab);
          }}
          onExtracted={() => review.reloadObjectives(true)}
        />
      ) : (
        <ChapterObjectivesTab
          review={review}
          onOpenObjective={(o) => openObjective(o)}
          onExtracted={onObjectivesExtracted}
          onShowLessons={() => onTabChange("lessons")}
        />
      );
    }
    if (tab === "lessons") {
      if (review.lessonsState === "loading") {
        return (
          <Stack spacing={1.5}>
            <Skeleton variant="rounded" height={48} />
            <Skeleton variant="rounded" height={160} />
            <Skeleton variant="rounded" height={160} />
          </Stack>
        );
      }
      if (review.lessonsState === "absent") {
        return (
          <EmptyState
            icon={<AccountTreeOutlinedIcon />}
            title="Pas de leçons"
            description="Ce document n'a pas été importé avec un manifeste : ses leçons ne sont pas connues. Réimportez-le comme chapitre."
          />
        );
      }
      if (review.lessonsState === "error" || !review.lessons) {
        return (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => review.reloadLessons()}>
                Réessayer
              </Button>
            }
          >
            Impossible de charger les leçons du chapitre.
          </Alert>
        );
      }
      return (
        <ChapterLessonsTab
          key={review.lessons.document_id}
          report={review.lessons}
          onOpenObjective={openObjectiveById}
        />
      );
    }
    if (tab === "review") {
      return (
        <ChapterRelectureTab
          documentId={document.id}
          onStatusChange={(status) => {
            if (status !== document.review_status) onChanged({ ...document, review_status: status });
          }}
        />
      );
    }
    if (!canEdit) return null;
    return <ChapterInfoTab document={document} tracks={names.tracksOfGrade(document.grade_id)} onSaved={onChanged} />;
  };

  const open = Boolean(document) || Boolean(loading) || Boolean(notFound);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: fullScreen ? "100vw" : "min(960px, 100vw)",
          bgcolor: "background.default",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box sx={{ bgcolor: "background.paper", borderBottom: 1, borderColor: "divider", px: { xs: 2, sm: 3 }, pt: 2 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {document ? (
              <>
                <Typography variant="h3" component="h2" dir="auto" sx={{ wordBreak: "break-word" }}>
                  {document.title}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                  {document.chapter_ref && (
                    <Typography variant="body2" className="mono" color="text.secondary">
                      {document.chapter_ref}
                    </Typography>
                  )}
                  <StatusPill tone={document.is_active ? "success" : "neutral"}>
                    {document.is_active ? "Actif" : "Inactif"}
                  </StatusPill>
                  {version != null && (
                    <StatusPill tone="neutral" dot={false} title="Version importée en cours">
                      v{version}
                    </StatusPill>
                  )}
                </Stack>
                {context.length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {context.join(" · ")}
                  </Typography>
                )}
              </>
            ) : notFound ? (
              <Typography variant="h3" component="h2">
                Chapitre introuvable
              </Typography>
            ) : (
              <>
                <Skeleton variant="text" width="60%" height={32} />
                <Skeleton variant="text" width="35%" />
              </>
            )}
          </Box>
          {document && (
            <Tooltip title="Télécharger le Markdown importé">
              <IconButton onClick={() => onDownload(document, "markdown")} aria-label="Télécharger le Markdown">
                <ArticleOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Fermer">
            <IconButton onClick={onClose} aria-label="Fermer">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Tabs
          value={tab}
          onChange={(_e, v: ChapterTab) => {
            setFocused(null);
            onTabChange(v);
          }}
          variant="scrollable"
          scrollButtons={false}
          sx={{ mt: 1.5, mx: -1.5 }}
        >
          <Tab
            value="objectives"
            label={
              review.objectivesState === "ok" && review.objectives.length > 0
                ? `Objectifs et faits (${review.objectives.length})`
                : "Objectifs et faits"
            }
            disabled={!document}
          />
          <Tab
            value="lessons"
            disabled={!document}
            label={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <span>
                  {review.lessonsState === "ok" && review.lessons
                    ? `Leçons (${review.lessons.summary.lessons})`
                    : "Leçons"}
                </span>
                {lessonIssues > 0 && (
                  <StatusPill tone={lessonIssuesSevere ? "error" : "warning"} dot={false} title="Leçons vides ou faibles">
                    {lessonIssues}
                  </StatusPill>
                )}
              </Stack>
            }
          />
          <Tab
            value="review"
            disabled={!document}
            label={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <span>Relecture</span>
                {(reviewStatus === "a_relire" || reviewStatus === "a_corriger") && (
                  <StatusPill tone={REVIEW_TONES[reviewStatus]} dot={false} title="Relecture humaine">
                    {REVIEW_LABELS[reviewStatus]}
                  </StatusPill>
                )}
              </Stack>
            }
          />
          {canEdit && <Tab value="info" label="Informations" disabled={!document} />}
        </Tabs>
      </Box>

      <Box ref={scrollRef} sx={{ flex: 1, overflowY: "auto", px: { xs: 2, sm: 3 }, py: 2.5 }}>
        {notFound ? (
          <EmptyState
            title="Ce chapitre n'est pas dans la liste"
            description="Il a peut-être été supprimé, ou il appartient à un autre programme."
            action={
              <Button variant="outlined" onClick={onClose}>
                Fermer
              </Button>
            }
          />
        ) : document ? (
          renderBody()
        ) : (
          <Stack spacing={1}>
            <Skeleton variant="rounded" height={48} />
            <Skeleton variant="rounded" height={48} />
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};

export default ChapterDrawer;
